import { Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useAddMember } from "@/hooks/useAddMember";
import { useMe } from "@/hooks/useMe";
import { useMembers } from "@/hooks/useMembers";
import { useRemoveMember } from "@/hooks/useRemoveMember";
import { useUpdateMemberRole } from "@/hooks/useUpdateMemberRole";
import type { PartnerMember, PartnerMemberRole } from "@/types/partner.types";

const ROLE_OPTIONS: { value: PartnerMemberRole; label: string }[] = [
	{ value: "owner", label: "Owner" },
	{ value: "admin", label: "Admin" },
	{ value: "member", label: "Member" },
];

interface AddMemberFormProps {
	onDone: () => void;
}

const AddMemberForm = (props: AddMemberFormProps) => {
	const { onDone } = props;
	const addMember = useAddMember();

	const [email, setEmail] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [role, setRole] = useState<PartnerMemberRole>("member");

	const canSubmit =
		email.trim() && firstName.trim() && lastName.trim() && !addMember.isPending;

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!canSubmit) return;
		addMember.mutate(
			{
				email: email.trim(),
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				role,
			},
			{
				onSuccess: () => {
					setEmail("");
					setFirstName("");
					setLastName("");
					setRole("member");
					onDone();
				},
			},
		);
	};

	return (
		<Card className="bg-clyr-gray-800 rounded-lg border-clyr-gray-700">
			<CardHeader className="p-4 pb-0">
				<CardTitle className="text-white text-base">Add member</CardTitle>
			</CardHeader>
			<CardContent className="p-4">
				<form
					onSubmit={handleSubmit}
					className="flex flex-wrap items-end gap-3"
				>
					<div className="space-y-2 flex-1 min-w-[200px]">
						<Label htmlFor="member-email" className="text-clyr-gray-300">
							Email
						</Label>
						<Input
							id="member-email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="bg-clyr-gray-900 border-clyr-gray-700 text-white"
						/>
					</div>
					<div className="space-y-2 w-40">
						<Label htmlFor="member-first" className="text-clyr-gray-300">
							First name
						</Label>
						<Input
							id="member-first"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							className="bg-clyr-gray-900 border-clyr-gray-700 text-white"
						/>
					</div>
					<div className="space-y-2 w-40">
						<Label htmlFor="member-last" className="text-clyr-gray-300">
							Last name
						</Label>
						<Input
							id="member-last"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							className="bg-clyr-gray-900 border-clyr-gray-700 text-white"
						/>
					</div>
					<div className="space-y-2 w-36">
						<Label className="text-clyr-gray-300">Role</Label>
						<Select
							value={role}
							onValueChange={(val) => setRole(val as PartnerMemberRole)}
						>
							<SelectTrigger className="bg-clyr-gray-900">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{ROLE_OPTIONS.map((opt) => (
									<SelectItem key={opt.value} value={opt.value}>
										{opt.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<Button type="submit" disabled={!canSubmit}>
						{addMember.isPending ? "Adding..." : "Add member"}
					</Button>
				</form>
			</CardContent>
		</Card>
	);
};

interface MemberRowProps {
	member: PartnerMember;
	canManage: boolean;
	isSelf: boolean;
}

const MemberRow = (props: MemberRowProps) => {
	const { member, canManage, isSelf } = props;
	const updateRole = useUpdateMemberRole();
	const removeMember = useRemoveMember();

	const fullName =
		[member.firstName, member.lastName].filter(Boolean).join(" ") || "—";

	const handleRemove = () => {
		if (
			!confirm(`Remove ${fullName} (${member.email}) from your partner team?`)
		) {
			return;
		}
		removeMember.mutate(member.id);
	};

	return (
		<TableRow className="border-clyr-gray-700/50">
			<TableCell className="text-white font-medium">
				{fullName}
				{isSelf && <span className="text-clyr-gray-500 ml-2">(you)</span>}
			</TableCell>
			<TableCell className="text-clyr-gray-300">{member.email}</TableCell>
			<TableCell>
				<Select
					value={member.role}
					disabled={!canManage || updateRole.isPending}
					onValueChange={(val) =>
						updateRole.mutate({
							memberId: member.id,
							role: val as PartnerMemberRole,
						})
					}
				>
					<SelectTrigger className="w-32">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{ROLE_OPTIONS.map((opt) => (
							<SelectItem key={opt.value} value={opt.value}>
								{opt.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</TableCell>
			<TableCell className="text-clyr-gray-400">
				{new Date(member.createdAt).toLocaleDateString()}
			</TableCell>
			<TableCell className="text-right">
				{canManage && (
					<Button
						variant="ghost"
						size="icon"
						onClick={handleRemove}
						disabled={removeMember.isPending}
						className="text-clyr-gray-400 hover:text-red-400 hover:bg-red-500/10"
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				)}
			</TableCell>
		</TableRow>
	);
};

export const MembersPage = () => {
	const { data: me } = useMe();
	const { data: members, isLoading } = useMembers();
	const [showAddForm, setShowAddForm] = useState(false);

	// Display-side gate only — the server enforces owner/admin regardless.
	const canManage = me?.member.role === "owner" || me?.member.role === "admin";

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Members</h1>
				{canManage && (
					<Button onClick={() => setShowAddForm((prev) => !prev)}>
						{showAddForm ? "Hide form" : "Add member"}
					</Button>
				)}
			</div>

			{canManage && showAddForm && (
				<AddMemberForm onDone={() => setShowAddForm(false)} />
			)}

			<div className="bg-clyr-gray-800 rounded-lg border border-clyr-gray-700 overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="border-clyr-gray-700 text-xs uppercase">
							<TableHead className="text-clyr-gray-400">Name</TableHead>
							<TableHead className="text-clyr-gray-400">Email</TableHead>
							<TableHead className="text-clyr-gray-400">Role</TableHead>
							<TableHead className="text-clyr-gray-400">Added</TableHead>
							<TableHead className="text-clyr-gray-400" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell
									colSpan={5}
									className="py-8 text-center text-clyr-gray-500"
								>
									Loading...
								</TableCell>
							</TableRow>
						) : !members?.length ? (
							<TableRow>
								<TableCell
									colSpan={5}
									className="py-8 text-center text-clyr-gray-500"
								>
									No members
								</TableCell>
							</TableRow>
						) : (
							members.map((member) => (
								<MemberRow
									key={member.id}
									member={member}
									canManage={canManage}
									isSelf={member.id === me?.member.id}
								/>
							))
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};
